/* eslint-disable @typescript-eslint/naming-convention */
import React, { useEffect, useState } from 'react';

import { getPlanStatus } from '../EmiPlans/helper';
import Button from '@/lib/Button';
import Modal from '@/lib/Modal';
import SecuredByRazorpay from '@/lib/SecuredByRazorpay';
import { getEmandateUrl } from '@/api/kyc';
import { getUserLoggedInStatus, trackHelper } from '@/analytics/utils';
import { ANALYTICS_ACTIONS, EVENT_NAMES } from '@/analytics/constants';
import { useGlobalStore } from '@/store';
import { EmiPlans } from '@/api/types';
import { PaymentCancelWrapper } from '@/shared/Components/PaymentCancelWrapper';

interface EMandatePopUpProps {
  onDismiss: () => void;
  cb: (url: string) => void;
}

const EMandatePopUp: React.FC<EMandatePopUpProps> = ({ onDismiss, cb }) => {
  const [isLoading, setIsLoading] = useState(false);
  const { activeView, user, userAccessToken, selectedTenure, lenderDetails } = useGlobalStore();
  const [showPaymentError, setShowPaymentError] = useState(false);

  const handleCTAClick = (): void => {
    setIsLoading(true);
    getEmandateUrl()
      .then((repaymentResponse) => {
        if (repaymentResponse.success && repaymentResponse.url) {
          cb(repaymentResponse.url);
          setIsLoading(false);
        } else {
          setIsLoading(false);
          setShowPaymentError(true);
        }

        // Track set up emandate response
        trackHelper({
          objectName: EVENT_NAMES.REPAYMENT_RECEIVED,
          actionName: ANALYTICS_ACTIONS.BACKEND_RESPONSE,
          screen: activeView,
          properties: {
            contact: user.contact,
            user_type: getUserLoggedInStatus(userAccessToken, user.contact),
            fc_customer_id: user.id,
            selectedPlan: selectedTenure,
            user_status: getPlanStatus(lenderDetails, selectedTenure as EmiPlans),
            repaymentResponse,
          },
        });
      })
      .catch((err) => {
        setIsLoading(false);
        setShowPaymentError(true);
        // Track set up emandate response error
        trackHelper({
          objectName: EVENT_NAMES.REPAYMENT_RECEIVED,
          actionName: ANALYTICS_ACTIONS.BACKEND_RESPONSE,
          screen: activeView,
          properties: {
            contact: user.contact,
            user_type: getUserLoggedInStatus(userAccessToken, user.contact),
            fc_customer_id: user.id,
            selectedPlan: selectedTenure,
            user_status: getPlanStatus(lenderDetails, selectedTenure as EmiPlans),
            error: err,
          },
        });
      });
    // Track set up emandate click
    trackHelper({
      objectName: EVENT_NAMES.SETUP_EMANDATE_CLICK,
      actionName: ANALYTICS_ACTIONS.CLICK,
      screen: activeView,
      properties: {
        contact: user.contact,
        user_type: getUserLoggedInStatus(userAccessToken, user.contact),
        fc_customer_id: user.id,
        selectedPlan: selectedTenure,
        user_status: getPlanStatus(lenderDetails, selectedTenure as EmiPlans),
      },
    });
  };

  useEffect(() => {
    // Track emandate screen render
    trackHelper({
      objectName: EVENT_NAMES.EMANDATE_POPUP_RENDER,
      actionName: ANALYTICS_ACTIONS.RENDER,
      screen: activeView,
      properties: {
        contact: user.contact,
        user_type: getUserLoggedInStatus(userAccessToken, user.contact),
        fc_customer_id: user.id,
        selectedPlan: selectedTenure,
        user_status: getPlanStatus(lenderDetails, selectedTenure as EmiPlans),
      },
    });
  }, []);

  return (
    <>
      <Modal isOpen={true} onDismiss={onDismiss}>
        <Modal.Body>
          <div className="d:text-center d:w-1/2">
            <div>
              <h2 className="font-tasa text-2xl text-on-grey-normal font-semibold mr-4 d:text-center">
                e-Mandate setup via Netbanking
              </h2>
              <p className="text-on-grey-normal font-normal text-base mt-1">
                To be created for seamless EMI payments (recurring).
              </p>
              <p className="mt-4 text-on-grey-subtle font-semibold">
                Please use a bank account that has the same name as your PAN and Aadhaar.
              </p>
            </div>
            <div className="mt-8 d:mt-2">
              <Button
                isLoading={isLoading}
                loadingText="Setting up e-mandate"
                onClick={handleCTAClick}
              >
                Set up e-Mandate
              </Button>
              <SecuredByRazorpay />
            </div>
          </div>
        </Modal.Body>
      </Modal>
      {showPaymentError ? (
        <PaymentCancelWrapper
          onDismiss={(): null => null}
          isOpen={showPaymentError}
          ctaText="Change payment method"
          title="E-mandate Failed"
          isCloseable={false}
          description="E-Mandate failed. Try with a different payment method"
        />
      ) : null}
    </>
  );
};

export default EMandatePopUp;
